// ILLUSTRATIVE FIGURES for the Automated Trading module. Not one number here is real.
//
// This portal has NO live trading data — no broker, no execution ledger, no price feed. The
// whole surface is invented, exactly as the AUM Dashboard is (see dashboard/dashboardData.js
// for the same treatment). Every figure the module renders lives in THIS file and nowhere
// else, so wiring it to a real source later is a change to one module rather than a hunt
// through the components. Each block names the real source it is WAITING for.
//
// This is a SEPARATE book from the AUM Dashboard. It deliberately reuses some of the same
// company names (Microsoft, Apple, ASML, Emirates NBD, Nestlé …) so the two illustrative
// books read as one house, but it does NOT import from dashboardData — that file is AED
// millions and organised by carrying value / liquidity band, whereas this is a USD trading
// book organised by security, GICS sector and strategy bucket, with realised + unrealised
// P&L. Keeping them independent is the point.
//
// DERIVATION RUNS UPWARDS: the only hand-typed numbers are the leaves — a holding's market
// value / cost / realised / unrealised, a cash balance, and the weekly return steps of the
// two return series. Everything a component reads (AUM, total P&L, allocation %, Sharpe,
// volatility, the equity curve) is derived below, so a changed leaf cannot leave a stale
// total, and the Sharpe can never disagree with the curve it is computed from.

const sum = (xs, f) => (xs || []).reduce((t, x) => t + (f(x) || 0), 0);

// ── EQUITY CURVE ─────────────────────────────────────────────────────────────────────────
// Waiting on: an EOD strategy-equity snapshot series (the Maghaweer `portfolio-twr` endpoint
// is the eventual analogue) and a benchmark (SPX) close series on the same dates.
//
// The LEAVES are two arrays of weekly percentage returns — the portfolio's and the
// benchmark's — chosen to look realistic (week-to-week noise, a few drawdowns) and to
// diverge so the overlay is visible: the portfolio out-trends SPX with a little more
// amplitude. The cumulative curves below compound these, REBASED so value 0 = window start
// (the shape MiniTwrChart expects). Sharpe and annualised volatility are then computed from
// these same weekly returns — so they describe this curve and no other. The window starts
// 2026-01-02 (first Friday) and steps weekly, so 35 steps land the last point at ~2026-09-04.

// 35 weekly returns (%), portfolio. Dispersion is real (drawdown weeks of −2 to −3%), so the
// derived Sharpe (~1.7) and annualised vol (~13%) read like a live systematic book rather
// than an implausibly smooth line. Compounds to roughly +16% over the window.
const PORT_WEEKLY_PCT = [
  2.2, 1.2, -2.5, 2.6, 0.8, -2.0, 2.1, 1.3, -1.7, -1.1, 2.5, 1.7, 0.7, -2.6, 2.6,
  1.4, -2.2, 2.3, 1.1, -2.8, -1.3, 2.4, 1.5, 0.9, -1.8, 2.0, 0.8, -2.3, 2.5, 1.2,
  -1.4, 1.6, 0.9, -1.0, 1.8,
];

// 35 weekly returns (%), SPX benchmark. Correlated in sign with the portfolio but lower in
// amplitude, so it shares the drawdowns yet ends clearly below (~+8%) — the dashed overlay
// stays legibly separate from the gold area throughout.
const SPX_WEEKLY_PCT = [
  1.5, 0.7, -1.9, 1.9, 0.5, -1.7, 1.5, 0.9, -1.4, -0.9, 2.0, 1.2, 0.4, -2.1, 2.1,
  0.9, -1.8, 1.6, 0.7, -2.2, -1.1, 1.7, 1.1, 0.6, -1.5, 1.4, 0.5, -1.9, 1.9, 0.8,
  -1.2, 1.1, 0.6, -0.9, 1.3,
];

// Weekly dates in UTC, one per point (start + N weekly steps). UTC avoids any local-timezone
// drift shifting a date across midnight at module load.
const isoWeek = (i) => {
  const t = Date.UTC(2026, 0, 2) + i * 7 * 86400000;
  const d = new Date(t);
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getUTCFullYear()}-${p(d.getUTCMonth() + 1)}-${p(d.getUTCDate())}`;
};

// Compound a weekly-return array into a rebased cumulative series: point 0 is the window
// start at 0.0, each later point is the compounded return since the start (a fraction, which
// is what MiniTwrChart plots and formats ×100).
const buildSeries = (weeklyPct) => {
  const pts = [{ date: isoWeek(0), value: 0 }];
  let cum = 0;
  weeklyPct.forEach((r, i) => {
    cum = (1 + cum) * (1 + r / 100) - 1;
    pts.push({ date: isoWeek(i + 1), value: cum });
  });
  return pts;
};

export const EQUITY_CURVE = buildSeries(PORT_WEEKLY_PCT); // [{ date, value }] portfolio TWR
export const BENCHMARK_CURVE = buildSeries(SPX_WEEKLY_PCT); // [{ date, value }] SPX, same dates
export const AS_AT = EQUITY_CURVE[EQUITY_CURVE.length - 1].date;

// Sharpe and annualised volatility, DERIVED from the portfolio's weekly returns (rf = 0, an
// illustrative simplification). Weekly returns → arithmetic mean and sample std → annualise
// by √52 (vol) and ×52 (return); Sharpe = annualised return / annualised vol. Computing them
// here, from the very array the curve compounds, is what guarantees the tile cannot show a
// Sharpe that contradicts the line above it.
const weeklyFractions = PORT_WEEKLY_PCT.map((r) => r / 100);
const mean = weeklyFractions.reduce((a, b) => a + b, 0) / weeklyFractions.length;
const variance = weeklyFractions.reduce((a, b) => a + (b - mean) ** 2, 0) / (weeklyFractions.length - 1);
const weeklyStd = Math.sqrt(variance);
export const ANN_VOL = weeklyStd * Math.sqrt(52);          // annualised volatility, fraction
export const SHARPE = (mean * 52) / ANN_VOL;               // rf = 0

// ── HOLDINGS ─────────────────────────────────────────────────────────────────────────────
// Waiting on: the strategy allocation tree / per-security fills ledger (Maghaweer's
// `strategy-allocation-tree` is the analogue). Leaves per holding: market value, cost basis,
// realised P&L (booked on exits/trims) and unrealised P&L (mark vs cost). Total P&L is their
// sum — done once, here, never re-subtracted in a component. `sector` is the GICS sector;
// `bucket` is the strategy class the position was opened under.
const RAW_HOLDINGS = [
  { symbol: 'MSFT', name: 'Microsoft Corp', sector: 'Information Technology', bucket: 'Momentum', marketValue: 980000, cost: 820000, realized: 15000, unrealized: 160000 },
  { symbol: 'AAPL', name: 'Apple Inc', sector: 'Information Technology', bucket: 'Momentum', marketValue: 890000, cost: 760000, realized: 8000, unrealized: 130000 },
  { symbol: 'NVDA', name: 'NVIDIA Corp', sector: 'Information Technology', bucket: 'Trend Following', marketValue: 760000, cost: 520000, realized: 22000, unrealized: 240000 },
  { symbol: 'JPM', name: 'JPMorgan Chase & Co', sector: 'Financials', bucket: 'Quality / Value', marketValue: 720000, cost: 650000, realized: 12000, unrealized: 70000 },
  { symbol: 'ASML', name: 'ASML Holding NV', sector: 'Information Technology', bucket: 'Quality / Value', marketValue: 640000, cost: 560000, realized: 0, unrealized: 80000 },
  { symbol: 'GOOGL', name: 'Alphabet Inc, Class A', sector: 'Communication Services', bucket: 'Momentum', marketValue: 620000, cost: 500000, realized: 11000, unrealized: 120000 },
  { symbol: 'TSM', name: 'Taiwan Semiconductor Mfg.', sector: 'Information Technology', bucket: 'Trend Following', marketValue: 610000, cost: 500000, realized: 5000, unrealized: 110000 },
  { symbol: 'ENBD', name: 'Emirates NBD Bank', sector: 'Financials', bucket: 'Mean Reversion', marketValue: 540000, cost: 480000, realized: 9000, unrealized: 60000 },
  { symbol: 'NESN', name: 'Nestlé SA', sector: 'Consumer Staples', bucket: 'Quality / Value', marketValue: 480000, cost: 470000, realized: 3000, unrealized: 10000 },
  { symbol: 'SHEL', name: 'Shell plc', sector: 'Energy', bucket: 'Mean Reversion', marketValue: 460000, cost: 430000, realized: 14000, unrealized: 30000 },
  { symbol: 'KO', name: 'Coca-Cola Co', sector: 'Consumer Staples', bucket: 'Market Neutral', marketValue: 420000, cost: 405000, realized: 6000, unrealized: 15000 },
  { symbol: 'UNH', name: 'UnitedHealth Group', sector: 'Health Care', bucket: 'Mean Reversion', marketValue: 390000, cost: 440000, realized: -8000, unrealized: -50000 },
  { symbol: 'MC', name: 'LVMH Moët Hennessy', sector: 'Consumer Discretionary', bucket: 'Quality / Value', marketValue: 360000, cost: 380000, realized: 4000, unrealized: -20000 },
  { symbol: 'FAB', name: 'First Abu Dhabi Bank', sector: 'Financials', bucket: 'Mean Reversion', marketValue: 330000, cost: 300000, realized: 5000, unrealized: 30000 },
];

// Waiting on: the account cash balance (model-account `cash_usd` analogue). Held flat here.
export const CASH = 1150000;

// --- derived, so a changed leaf above cannot leave a stale total below ---------------------

// Each holding with its total P&L computed once. Sorted by market value, largest first — a
// reader scanning the holdings table looks for the biggest position, not the alphabetically
// first one. `pct` is share of the DEPLOYED book (market values sum to 100%), which is what
// the allocation bar and the donut both read.
export const DEPLOYED = sum(RAW_HOLDINGS, (h) => h.marketValue);

export const HOLDINGS = RAW_HOLDINGS
  .map((h) => ({ ...h, pnl: h.realized + h.unrealized, pct: DEPLOYED ? h.marketValue / DEPLOYED : 0 }))
  .sort((a, b) => b.marketValue - a.marketValue);

export const AUM = DEPLOYED + CASH;
export const CASH_PCT = AUM ? (CASH / AUM) * 100 : null;

// Book P&L, and the total for the KPI tile — the sum of every holding's realised + unrealised.
export const BOOK_PNL = {
  realized: sum(HOLDINGS, (h) => h.realized),
  unrealized: sum(HOLDINGS, (h) => h.unrealized),
  total: sum(HOLDINGS, (h) => h.pnl),
};
export const TOTAL_PNL = BOOK_PNL.total;

// Group market value by an arbitrary key (sector or bucket), share of deployed book, DESC by
// value. Used for the Sector and Bucket tables and their donuts — allocation is always
// derived from market value, never transcribed.
const groupBy = (key) => {
  const m = new Map();
  HOLDINGS.forEach((h) => m.set(h[key], (m.get(h[key]) || 0) + h.marketValue));
  return [...m.entries()]
    .map(([label, marketValue]) => ({ label, marketValue, pct: DEPLOYED ? marketValue / DEPLOYED : 0 }))
    .sort((a, b) => b.marketValue - a.marketValue);
};

export const BY_SECTOR = groupBy('sector');
export const BY_BUCKET = groupBy('bucket');
